// ScaleBar — minimaler, UI-konsistenter Maßstabs-Indikator für die
// untere Map-Leiste. Ersetzt MapLibres native `ScaleControl`, weil:
//   1) das native Control ein eigenes weißes Chrome mitbringt (border +
//      background) und sich nicht sauber in unsere dunkle Pill integriert;
//   2) wir es ohnehin innerhalb der vereinheitlichten Bottom-Bar rendern
//      wollen, nicht als freischwebender Layer unten-links.
//
// Rechenweg:
//   metersPerPixel(zoom, lat) = 156543.03392 * cos(lat * π/180) / 2^zoom
//   (Spherical Mercator, gleiches Modell wie MapLibre's ScaleControl.)
// Wir picken dann eine "nice"-Länge ≤ 100 px (1, 2, 5, 10, 20, 50, 100, 200,
// 500, 1000, 2000, ...) und zeichnen einen Strich von entsprechender Breite.
//
// Subscribt auf `move`-Events und re-rendert bei jedem Zoom/Pan. Billig, da
// die Komponente einen winzigen DOM-Footprint hat (div + span).

import { useEffect, useState } from 'react';
import type { Map as MLMap } from 'maplibre-gl';
import { mapBus } from '../lib/map/mapBus';

/** Max-Breite des Scale-Strichs in Pixeln. 70 ist kompakt genug für die Pill. */
const MAX_WIDTH_PX = 70;

/** Zulässige "nice"-Werte in Metern (ambroise von 1 m bis 1000 km). */
const NICE_STEPS = [
  1, 2, 5,
  10, 20, 50,
  100, 200, 500,
  1000, 2000, 5000,
  10000, 20000, 50000,
  100000, 200000, 500000, 1000000,
];

function computeScale(map: MLMap): { label: string; widthPx: number } {
  const zoom = map.getZoom();
  const lat = map.getCenter().lat;
  const metersPerPixel =
    (156543.03392 * Math.cos((lat * Math.PI) / 180)) / Math.pow(2, zoom);
  const maxMeters = metersPerPixel * MAX_WIDTH_PX;

  // Größter "nice"-Wert, der noch unter maxMeters liegt. Fallback 1 m für
  // den Extremfall metersPerPixel ≈ 0 (falls MapLibre pre-load eine Zoom-
  // Range zurückgibt, in der kein Step passt — nie in der Praxis, aber
  // der Compiler weiß das nicht mit noUncheckedIndexedAccess).
  let chosen = 1;
  for (const step of NICE_STEPS) {
    if (step > maxMeters) break;
    chosen = step;
  }
  const widthPx = Math.max(16, Math.round(chosen / metersPerPixel));
  const label = chosen >= 1000 ? `${chosen / 1000} km` : `${chosen} m`;
  return { label, widthPx };
}

export function ScaleBar() {
  const [state, setState] = useState<{ label: string; widthPx: number } | null>(null);

  useEffect(() => {
    let currentMap: MLMap | null = null;
    let lastMoveHandler: (() => void) | null = null;

    const attachTo = (map: MLMap | null) => {
      // Vorherige Subscription aufräumen.
      if (currentMap && lastMoveHandler) {
        currentMap.off('move', lastMoveHandler);
        currentMap.off('zoom', lastMoveHandler);
      }
      currentMap = map;
      lastMoveHandler = null;

      if (!map) {
        setState(null);
        return;
      }
      const update = () => setState(computeScale(map));
      lastMoveHandler = update;
      map.on('move', update);
      map.on('zoom', update);
      // Initialer Wert — falls die Map schon geladen ist.
      if (map.loaded()) {
        update();
      } else {
        map.once('load', update);
      }
    };

    attachTo(mapBus.get());
    const unsub = mapBus.subscribe(attachTo);

    return () => {
      unsub();
      if (currentMap && lastMoveHandler) {
        currentMap.off('move', lastMoveHandler);
        currentMap.off('zoom', lastMoveHandler);
      }
    };
  }, []);

  if (!state) {
    // Platzhalter mit fester Breite, damit die Pill nicht beim ersten
    // Map-Load-Frame "hüpft". Gleiche Breite wie ein typisches "500 m" Label.
    // Wave 14cb-fix4: text-[11px] → text-sm (User-Wunsch "die distanz neben
    // der skala größere schrift").
    return (
      <div
        className="flex items-center gap-1.5 text-sm font-medium text-white/70 tabular-nums select-none"
        aria-hidden="true"
      >
        <span className="opacity-60">— m</span>
      </div>
    );
  }

  return (
    <div
      className="flex items-center gap-1.5 text-sm font-medium text-white/85 tabular-nums select-none"
      title={`Scale: ${state.label}`}
    >
      <span>{state.label}</span>
      {/* Doppel-Strich-Indikator — zwei vertikale Ticks rahmen die horizontale
          Linie ein, matcht das visuelle Modell einer klassischen Karten-Scale. */}
      <span
        className="relative h-[10px] flex items-center"
        style={{ width: `${state.widthPx}px` }}
      >
        <span className="absolute left-0 top-0 bottom-0 w-[1.5px] bg-current opacity-75" />
        <span className="absolute left-0 right-0 top-1/2 h-[1.5px] -translate-y-1/2 bg-current opacity-75" />
        <span className="absolute right-0 top-0 bottom-0 w-[1.5px] bg-current opacity-75" />
      </span>
    </div>
  );
}
